/**
 * The one HTTP wrapper both backends share.
 *
 * - base 'clinic': cms-backend /v1 — Bearer + x-clinic-id on every call.
 * - base 'auth': Auth service — Bearer only where the call needs it.
 * - 401 on an authenticated call → single silent refresh → one retry.
 * - Failures become typed ApiError / NetworkError; bodies are never
 *   reshaped — the backend contract is authoritative.
 * - ZOY-466: every request carries the shared 12s deadline, so a STALLED
 *   request settles as a TimeoutError instead of hanging forever. A caller's
 *   own abort still propagates raw, exactly as before.
 */

import { runtimeConfig } from '../config';
import {
  CLINIC_CONTEXT_LOST_CODES,
  frozenClinicContext,
  getSelectedClinicId,
  notifyClinicContextLost,
  type ClinicContextLostCode,
} from '../authz/clinicContext';
import {
  ApiError,
  ClinicContextChangedError,
  NetworkError,
  TimeoutError,
  parseErrorBody,
} from './errors';
import { isAbortError, withDeadline } from './deadline';
import {
  accessTokenStale,
  getAccessToken,
  getStoredRefreshToken,
  refreshAccessToken,
} from '../auth/tokens';

export interface RequestOptions {
  /** `chat` (ZOY-696): the Chat platform, with the clinic token and no clinic header. */
  base: 'clinic' | 'auth' | 'chat';
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Path starting with '/'. Clinic paths include /v1 explicitly. */
  path: string;
  body?: unknown;
  /** Attach Authorization (default true). */
  auth?: boolean;
  /** Attach x-clinic-id on clinic calls (default true). */
  clinicHeader?: boolean;
  /** Extra query params. */
  query?: Record<string, string | undefined>;
  /** Override the clinic header value (e.g. reading names for the chooser). */
  clinicIdOverride?: string;
  /** Extra request headers (e.g. `Idempotency-Key` on a Plan write). */
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

function origin(base: 'clinic' | 'auth' | 'chat'): string {
  const cfg = runtimeConfig();
  if (base === 'chat') {
    if (!cfg.chatApiUrl) throw new Error('chat platform origin is not configured');
    return cfg.chatApiUrl;
  }
  return base === 'clinic' ? cfg.clinicApiUrl : cfg.authApiUrl;
}

async function ensureFreshAccessToken(): Promise<string | null> {
  if (!accessTokenStale()) return getAccessToken();
  if (getStoredRefreshToken() === null) return getAccessToken();
  try {
    return await refreshAccessToken();
  } catch {
    return getAccessToken();
  }
}

async function performOnce<T>(opts: RequestOptions, token: string | null): Promise<T> {
  const url = new URL(origin(opts.base) + opts.path);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined) url.searchParams.set(k, v);
    }
  }
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if ((opts.auth ?? true) && token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.base === 'clinic' && (opts.clinicHeader ?? true)) {
    const clinic = opts.clinicIdOverride ?? getSelectedClinicId();
    if (clinic) headers['x-clinic-id'] = clinic;
  }

  const deadline = withDeadline(opts.signal);
  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: deadline.signal,
    });
  } catch (e) {
    // Order matters: our own timeout also arrives as an AbortError, and must
    // become a typed error rather than escape as a raw DOMException.
    if (deadline.timedOut()) throw new TimeoutError();
    if (isAbortError(e)) throw e; // the caller cancelled — unchanged behaviour
    throw new NetworkError();
  } finally {
    deadline.done();
  }

  if (!res.ok) {
    const parsed = await res.json().catch(() => null);
    const err = parseErrorBody(res.status, parsed, res.headers.get('Retry-After'));
    // ZOY-610: the selected clinic is no longer one this person may work in
    // (membership ended / clinic deactivated) — on ANY clinic request. The
    // authz provider re-resolves; the caller still gets its typed error.
    if (
      opts.base === 'clinic' &&
      typeof err.code === 'string' &&
      CLINIC_CONTEXT_LOST_CODES.includes(err.code)
    ) {
      notifyClinicContextLost(err.code as ClinicContextLostCode);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function apiRequest<T>(opts: RequestOptions): Promise<T> {
  // ZOY-610: another tab switched clinic — this tab issues nothing more
  // against a context the person left, until it is reloaded or signed out.
  // Only requests that CARRY a clinic selection are frozen: an identity,
  // discovery or anonymous read (clinicHeader:false) names no clinic, so it
  // cannot reach the wrong one — and the /c/<slug> resolver must keep working.
  if (opts.base === 'clinic' && (opts.clinicHeader ?? true) && frozenClinicContext() !== null) {
    throw new ClinicContextChangedError();
  }
  const wantsAuth = opts.auth ?? true;
  const token = wantsAuth ? await ensureFreshAccessToken() : null;
  try {
    return await performOnce<T>(opts, token);
  } catch (err) {
    // One silent refresh + retry on 401 for authenticated calls.
    if (
      wantsAuth &&
      err instanceof ApiError &&
      err.status === 401 &&
      getStoredRefreshToken() !== null
    ) {
      const fresh = await refreshAccessToken(); // clears session + throws on refusal
      return performOnce<T>(opts, fresh);
    }
    throw err;
  }
}
